import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  formatDuration,
  intervalToDuration,
} from "date-fns";
import * as R from "ramda";
import React from "react";
import { PickByKeys } from "state/options";

export type StateSetter<T> = React.Dispatch<React.SetStateAction<T>>;

export const humanDuration = (seconds: number) => {
  const durations = intervalToDuration({ start: 0, end: seconds * 1000 });
  return formatDuration(durations);
};

export const rTry = (func: () => any, fallback: any) => {
  return R.tryCatch(func, (e) => {
    console.error(e);
    return fallback;
  })();
};

export const humanDt = (date?: Date) => {
  if (!date) return null;

  return format(date, "MMM d, yyyy h:mm a");
};

export const pluralise = (str: string, count: number) =>
  count === 1 ? str : str + "s";

export const fileNameFromBucketPath = (path: string) =>
  path?.split("/").slice(3)?.[0]?.slice(37);

export const stripNils = <T extends Record<string, any>>(thing: T): T => {
  return Object.fromEntries(
    Object.entries(thing).filter(([_, val]) => Boolean(val)),
  ) as any;
};

export const isNil = (val: any) => val === null || val === undefined;

export const pick = <T extends Object, K extends (keyof T)[]>(
  obj: T,
  keys: K,
): PickByKeys<T, K[number]> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([k, v]) => keys.includes(k as keyof T)),
  ) as PickByKeys<T, K[number]>;
};

export function update<T, K extends keyof T>(obj: T, path: K, value: T[K]): T;
export function update<T, K extends (keyof T)[]>(
  obj: T,
  path: K,
  value: any,
): T;
export function update(obj: undefined, path: any, value: any): undefined;

export function update(obj, keys, val) {
  if (!obj) return obj;

  if (Array.isArray(keys)) {
    return R.assocPath(keys, val, obj);
  } else {
    R.assoc(keys, val, obj);
  }
}

export function toInt(
  maybeInt: string | number | undefined | null,
  nullIsZero: true,
): number;

export function toInt(
  maybeInt: string | number | undefined | null,
  nullIsZero: false,
): number | null;
export function toInt(maybeInt, nullIsZero = false) {
  if (Number.isInteger(maybeInt)) return maybeInt as number;
  if (typeof maybeInt === "number") return Math.floor(maybeInt) as number;
  if (typeof maybeInt === "string") return parseInt(maybeInt);
  return nullIsZero ? 0 : null;
}

export function toFloat(
  maybeInt: string | number | undefined | null,
  nullIsZero: true,
): number;

export function toFloat(
  maybeInt: string | number | undefined | null,
  nullIsZero: false,
): number | null;
export function toFloat(maybeInt, nullIsZero = false) {
  if (typeof maybeInt === "number") return maybeInt as number;
  if (typeof maybeInt === "string") return parseFloat(maybeInt);
  return nullIsZero ? 0 : null;
}

export const maybeAddHttps = (url: string) =>
  url.startsWith("http") ? url : "https://" + url;

export const toPct = (total: number, val: number) =>
  Math.round(((total - val) / total) * 100);

export const toLum = <T>(
  data: Record<string, any>[],
  key: string,
): Record<string, T> =>
  data.reduce((acc, next) => ({ ...acc, [next[key]]: next }), {});

export const inspecto = <T>(val: T): T => {
  console.dir(val, { depth: null });
  return val;
};

export function numberWithCommas(x: number | undefined) {
  if (!x) return "0";
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Excepts cents
export function toCurrencyAmount(
  currency: "GBP" | "USD" | "EURO",
  x: number | undefined,
) {
  const symbol = currency === "GBP" ? "£" : currency === "EURO" ? "€" : "$";
  return `${symbol}${numberWithCommas((x || 0) / 100)}`;
}

export interface TimeDiffs {
  unit: "days" | "hours" | "minutes";
  value: number;
  all: {
    inMinutes: number;
    inHours: number;
    inDays: number;
  };
}
export const diffInBestUnit = (
  date1: string | Date,
  date2: string | Date,
): TimeDiffs | undefined => {
  if (!date1 || !date2) return undefined;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const inMinutes = differenceInMinutes(d1, d2);
  const inHours = differenceInHours(d1, d2);
  const inDays = differenceInDays(d1, d2);
  const all = { inMinutes, inHours, inDays };

  if (inMinutes < 120) {
    return { unit: "minutes", value: inMinutes, all };
  } else if (inMinutes < 2880) {
    return { unit: "hours", value: inHours, all };
  } else {
    return { unit: "days", value: inDays, all };
  }
};

// Takes a row of records and an identifier + field and merges the provided object into that record matching the field + identifier in the array
// mergeRecordInArray([{a: 1}, {b: 2}], 'b', 2, {c: 3, b: 4})
// => [{a: 1}, {b: 4, c: 3}]
export const mergeRecordInArray = <T>(
  rows: T[],
  field: keyof T,
  identifier: any,
  val: T,
): T[] =>
  // Seriously fuck ramda and it's lack of TS support
  R.over(
    R.lensPath([R.findIndex((r: T) => r[field] === identifier, rows)]),
    (record: T) => ({ ...record, ...val }),
    rows,
  );

export const toColor = (
  text?: string,
  array = [
    "blue",
    "orange",
    "p",
    "teal",
    "pink",
    "green",
    "gray",
    "red",
    "cyan",
  ],
) =>
  text ? array[Math.max((hashString(text) % array.length) - 1, 0)] : "gray";

const hashString = (s: string): number => {
  const n = s.split("").reduce(function (a: any, b: any) {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
  return n >= 0 ? n : n * -1;
};

export const upperFirst = (str: string) =>
  str && str[0].toUpperCase() + str.slice(1);

interface Nameable {
  firstName?: string | null;
  lastName?: string | null;
}

export const toName = (nameable: Nameable | null): string =>
  nameable
    ? nameable.lastName && nameable.firstName
      ? `${nameable.firstName} ${nameable.lastName}`
      : nameable.firstName || ""
    : "";

export const toDayMonth = (date: Date) => format(date, "d MMM");

// Naming is hard basically used when you want arrow up and down through a list and
// when you get to the top you want it to go to the bottom and vice versa
export const loopArrayIdx = (
  current: number,
  total: number,
  direction: "up" | "down",
) => {
  if (direction === "up") {
    return current > 0 ? current - 1 : total - 1;
  } else {
    return current < 0 ? current + 1 : (current + 1) % total;
  }
};

export const formatAsDate = (date: Date) => format(date, "yyyy-MM-dd");

export const checkInView = (container, element, partial) => {
  //Get container properties
  let cTop = container.scrollTop;
  let cBottom = cTop + container.clientHeight;

  //Get element properties
  let eTop = element.offsetTop;
  let eBottom = eTop + element.clientHeight;

  //Check if in view
  let isTotal = eTop >= cTop && eBottom <= cBottom;
  let isPartial =
    partial &&
    ((eTop < cTop && eBottom > cTop) || (eBottom > cBottom && eTop < cBottom));

  //Return outcome
  return isTotal || isPartial;
};

export const tagColor = (name: string) => {
  const n = name.split("")[0].toLowerCase().charCodeAt(0) - 97;
  if (n < 3) {
    return "red";
  } else if (n < 6) {
    return "darkBlue";
  } else if (n < 9) {
    return "yellow";
  } else if (n < 12) {
    return "cyan";
  } else if (n < 15) {
    return "purple";
  } else if (n < 18) {
    return "green";
  } else if (n < 20) {
    return "pink";
  } else {
    return "orange";
  }
};

const toSnakeCase = (str) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

// Main function to recursively convert object keys
export const snakeIt = (obj, ignoreKeys: string[] = []) => {
  if (typeof obj !== "object" || obj === null) {
    // Return non-object values as is
    return obj;
  }

  if (Array.isArray(obj)) {
    // Recursively process each element in an array
    return obj.map((v) => snakeIt(v, ignoreKeys));
  }

  // Process each key/value pair in the object
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (ignoreKeys?.includes(key)) {
      acc[key] = value;
      return acc;
    } else {
      // Convert the key and recursively call the function for the value
      acc[toSnakeCase(key)] = snakeIt(value, ignoreKeys);
      return acc;
    }
  }, {});
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (str) =>
  str.replace(/(_[a-z])/g, (group) => group[1].toUpperCase());

// Main function to recursively convert object keys
export const camelIt = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    // Return non-object values as is
    return obj;
  }

  if (Array.isArray(obj)) {
    // Recursively process each element in an array
    return obj.map(camelIt);
  }

  // Process each key/value pair in the object
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Convert the key and recursively call the function for the value
    acc[toCamelCase(key)] = camelIt(value);
    return acc;
  }, {});
};

export const errorChecker = <T extends Record<string, any>>(
  params: T,
  validator: Partial<
    Record<keyof T, ((val: any) => string) | undefined | "required">
  >,
): string | undefined => {
  const errors = Object.entries(params)
    .map(([key, val]): [string, string | undefined] | [] => {
      const v = validator[key];
      if (!v) return [];

      if (typeof v === "function") {
        return [key, v(val)];
      } else if (v === "required") {
        return [key, val.length > 1 ? undefined : "Required"];
      } else {
        return [];
      }
    })
    .filter((res) => res?.length === 2);

  if (errors.length === 0) {
    return undefined;
  } else {
    const errString = errors
      .map(
        (row) =>
          row[0] &&
          row[1] &&
          `${upperFirst(row[0].replaceAll("_", " "))}: ${row[1] || ""}`,
      )
      .join("\n")
      .trim();

    return errString.length < 1 ? undefined : errString;
  }
};
